import React, { useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Snackbar,
  Alert,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
// Use API_BASE_URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
import { downloadDailyFeeReportPDF } from "./DailyFeeReportPDF";

// Utility to fetch college details
async function fetchCollegeDetails(clientId, collegeId) {
  try {
    // Correct API path for college by collegeId (plural)
    const res = await axios.get(`${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`);
    if (res.data && res.data.success) {
      return res.data.data;
    }
  } catch (e) {
    console.error("Failed to fetch college details", e);
  }
  return null;
}

const paymentModes = [
  { value: "", label: "All" },
  { value: "CASH", label: "CASH" },
  { value: "ONLINE", label: "ONLINE" },
];

// Helper function to format dates consistently
const formatDate = (dateValue) => {
  if (!dateValue) return "";

  try {
    // Handle different date formats
    let dateObj;

    if (typeof dateValue === "object" && dateValue !== null) {
      // If it's already a Date object or has toISOString method
      if (dateValue.toISOString) {
        dateObj = new Date(dateValue);
      } else {
        // Try to convert to string and parse
        dateObj = new Date(String(dateValue));
      }
    } else {
      // Try to parse the string value
      dateObj = new Date(dateValue);
    }

    // Check if date is valid
    if (!isNaN(dateObj.getTime())) {
      // Format as DD-MM-YYYY (change format if needed)
      const day = String(dateObj.getDate()).padStart(2, "0");
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const year = dateObj.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch (e) {
    console.error("Date formatting error:", e);
  }

  // If all else fails, return as string
  return String(dateValue || "");
};

export default function DailyFeeReport() {
  const [groupByMode, setGroupByMode] = useState(false);
  const [groupRows, setGroupRows] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [collegeDetails, setCollegeDetails] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);

  const fetchGroupByReport = async () => {
    setGroupLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const clientId = user?.client_id || 1;
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await axios.get(`${API_BASE_URL}/api/dailyfeereport/groupby/${clientId}`, {
        params,
      });
      if (res.data && res.data.success) {
        setGroupRows(
          res.data.data.map((row, idx) => ({ id: idx + 1, serial_no: idx + 1, ...row }))
        );
      } else {
        setGroupRows([]);
        setSnackbarMessage("No data found");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setGroupRows([]);
      setSnackbarMessage("Error fetching group report");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setGroupLoading(false);
    }
  };
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Utility to convert image URL to base64
  function toBase64(url) {
    return fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  }

  // Fetch college details and logo on mount
  React.useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user") || "null");
    const clientId = user?.client_id;
    const collegeId = user?.college_id;
    if (clientId && collegeId) {
      fetchCollegeDetails(clientId, collegeId).then((college) => {
        setCollegeDetails(college);
        if (college && college.college_logopath && college.society_id && college.college_id) {
          const logoPath = `/uploads/logos/${clientId}/${college.society_id}/${college.college_id}/${college.college_logopath}`;
          toBase64(logoPath)
            .then((base64) => setLogoBase64(base64))
            .catch(() => setLogoBase64(null));
        } else {
          setLogoBase64(null);
        }
      });
    }
  }, []);

  const columns = [
    { field: "serial_no", headerName: "S.No", width: 80 },
    {
      field: "receipt_date",
      headerName: "Receipt Date",
      width: 140,
      valueFormatter: (params) => formatDate(params.value),
    },
    { field: "student_rollno", headerName: "Roll No", width: 140 },
    { field: "fee_head_name", headerName: "Fee Head Name", width: 180 },
    {
      field: "paid_amount",
      headerName: "Paid Amount",
      width: 140,
      valueFormatter: (params) => {
        // Format as currency if needed
        return params.value ? `₹ ${params.value}` : params.value;
      },
    },
    { field: "remarks", headerName: "Remarks", width: 200 },
  ];

  const fetchReport = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const clientId = user?.client_id || 1;
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (paymentMode) params.payment_mode = paymentMode;

      const res = await axios.get(`${API_BASE_URL}/api/dailyfeereport/${clientId}`, { params });

      if (res.data && res.data.success) {
        let processedRows = res.data.data.map((row, idx) => ({
          ...row,
          id: idx + 1,
          serial_no: idx + 1,
          receipt_date: formatDate(row.receipt_date), // Always store as formatted string
        }));

        // Sort by receipt_date ascending (DD-MM-YYYY format)
        processedRows = processedRows.sort((a, b) => {
          // Parse DD-MM-YYYY to Date
          const [ad, am, ay] = a.receipt_date.split("-");
          const [bd, bm, by] = b.receipt_date.split("-");
          const dateA = new Date(`${ay}-${am}-${ad}`);
          const dateB = new Date(`${by}-${bm}-${bd}`);
          return dateA - dateB;
        });

        setRows(processedRows);
      } else {
        setRows([]);
        setSnackbarMessage("No data found");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setRows([]);
      setSnackbarMessage("Error fetching report");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Daily Fee Report
          </Typography>
        </Toolbar>

        {/* Filter controls */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="Payment Mode"
            name="payment_mode"
            select
            // fullWidth removed for small dropdown
            value={paymentMode}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
            onChange={(e) => setPaymentMode(e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <option value="">All</option>
            <option value="CASH">CASH</option>
            <option value="ONLINE">ONLINE</option>
          </TextField>
          <Button
            variant="contained"
            onClick={fetchReport}
            sx={{ minWidth: 140, color: "#fff" }}
            disabled={loading || groupByMode}
          >
            {loading ? <CircularProgress size={24} /> : "Generate Report"}
          </Button>
          <Button
            variant="outlined"
            sx={{ minWidth: 140, color: "#000", fontWeight: 500 }}
            onClick={() => {
              downloadDailyFeeReportPDF({
                rows,
                columns,
                fromDate,
                toDate,
                paymentMode,
                collegeDetails,
                logoBase64,
              });
            }}
            disabled={rows.length === 0 || groupByMode}
          >
            Download PDF
          </Button>
          <Button
            variant={groupByMode ? "contained" : "outlined"}
            sx={{
              minWidth: 140,
              color: groupByMode ? "#fff" : "#000",
              fontWeight: 500,
              backgroundColor: groupByMode ? "#1976d2" : undefined,
            }}
            onClick={() => {
              if (!groupByMode) {
                fetchGroupByReport();
              }
              setGroupByMode(!groupByMode);
            }}
          >
            {groupByMode ? "Show Normal Report" : "Group By Report"}
          </Button>
        </Box>

        {/* Data grid or group by table */}
        {!groupByMode ? (
          <Box sx={{ height: 400, width: "100%" }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
                initialState={{
                  pagination: {
                    pageSize: 10,
                  },
                }}
              />
            )}
          </Box>
        ) : (
          <Box sx={{ height: 400, width: "100%" }}>
            {groupLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={groupRows}
                columns={[
                  { field: "serial_no", headerName: "S.No", width: 80 },
                  { field: "fee_type", headerName: "Fee Type", flex: 1 },
                  {
                    field: "total_fee_amount",
                    headerName: "Fee Amount Total",
                    flex: 1,
                    valueFormatter: (params) => `₹ ${Number(params.value).toLocaleString("en-IN")}`,
                  },
                ]}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
              />
            )}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
